// Was auf der Tafel an welchem Tag steht.
//
// Drei Quellen, ein Zettelstapel je Tag:
//   eigen     Aufgaben, Termine, Fristen, die jemand angelegt hat
//   betrieb   automatische Aufgaben (Nachbestellen, Mahnen …) — überfällige
//             landen auf heute, mit rotem Rand
//   frist     Fristen aus den Daten (Rechnung fällig, UStVA am 10. …)
//
// Was schon als automatische Aufgabe dasteht, kommt nicht noch einmal als
// Frist: beide tragen denselben Bezug (Rechnung, Ausgabe, Beleg, Monat).
//
// Bewusst ohne Importe außer Typen — so laufen die Tests direkt mit node.
import type { Aufgabe, AutoAufgabe, Frist } from '../../../gemeinsam/lib/typen';

export type Art = 'aufgabe' | 'termin' | 'frist';

export type Eintrag = {
  schluessel: string;
  quelle: 'eigen' | 'betrieb' | 'frist';
  /** JJJJ-MM-TT, leer = Ablage (ohne Datum) */
  datum: string;
  art: Art;
  uhrzeit: string;
  fertig: boolean;
  ueberfaellig: boolean;
  aufgabe?: Aufgabe;
  auto?: AutoAufgabe;
  frist?: Frist;
};

/** JJJJ-MM-TT im lokalen Kalender. */
export const tag = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const alsDatum = (t: string) => new Date(`${t}T12:00:00`);

export function plus(t: string, n: number): string {
  const d = alsDatum(t);
  d.setDate(d.getDate() + n);
  return tag(d);
}

/** Montag der Woche. */
export function wochenStart(t: string): string {
  const wt = (alsDatum(t).getDay() + 6) % 7; // Mo = 0
  return plus(t, -wt);
}

export const wochenTage = (start: string) => Array.from({ length: 7 }, (_, i) => plus(start, i));

/** Sechs Wochen ab dem Montag vor dem Ersten — das Raster einer Monatsansicht. */
export function monatsRaster(t: string): string[] {
  const erster = `${t.slice(0, 7)}-01`;
  const start = wochenStart(erster);
  return Array.from({ length: 42 }, (_, i) => plus(start, i));
}

/** Kalenderwoche nach ISO 8601. */
export function kalenderwoche(t: string): number {
  const d = alsDatum(t);
  const donnerstag = new Date(d);
  donnerstag.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const ersterDo = new Date(donnerstag.getFullYear(), 0, 4, 12);
  return 1 + Math.round(((donnerstag.getTime() - ersterDo.getTime()) / 86400000 - 3 + ((ersterDo.getDay() + 6) % 7)) / 7);
}

const FRIST_TYPEN = new Set(['mahnen', 'bezahlen', 'ustva']);
const PRIO = { high: 0, medium: 1, low: 2 } as const;
const ARTRANG: Record<Art, number> = { termin: 0, frist: 1, aufgabe: 2 };

export function eintraegeBauen(aufgaben: Aufgabe[], auto: AutoAufgabe[], fristen: Frist[], heute: string): Eintrag[] {
  const liste: Eintrag[] = [];
  for (const a of aufgaben) {
    const datum = a.dueDate || '';
    const fertig = (a.column || 'todo') === 'done';
    liste.push({
      schluessel: a.id, quelle: 'eigen', datum, art: a.art || 'aufgabe', uhrzeit: a.uhrzeit || '',
      fertig, ueberfaellig: !fertig && !!datum && datum < heute, aufgabe: a,
    });
  }
  const bezuege = new Set<string>();
  for (const a of auto) {
    bezuege.add(a.schluessel);
    if (a.ziel?.id) bezuege.add(a.ziel.id);
    if (a.aktion?.id) bezuege.add(a.aktion.id);
    const ueberfaellig = !!a.faellig && a.faellig < heute;
    liste.push({
      schluessel: `auto:${a.schluessel}`, quelle: 'betrieb', datum: ueberfaellig || !a.faellig ? heute : a.faellig,
      art: FRIST_TYPEN.has(a.typ) ? 'frist' : 'aufgabe', uhrzeit: '', fertig: false, ueberfaellig, auto: a,
    });
  }
  for (const f of fristen) {
    if (bezuege.has(f.bezug)) continue;
    liste.push({ schluessel: f.schluessel, quelle: 'frist', datum: f.datum, art: 'frist', uhrzeit: '', fertig: false, ueberfaellig: false, frist: f });
  }
  return liste;
}

/** Reihenfolge auf einem Tag: Termine nach Uhrzeit, dann Fristen, dann Aufgaben — Erledigtes zuletzt. */
export function sortiert(liste: Eintrag[]): Eintrag[] {
  return [...liste].sort((a, b) =>
    Number(a.fertig) - Number(b.fertig)
    || Number(b.ueberfaellig) - Number(a.ueberfaellig)
    || ARTRANG[a.art] - ARTRANG[b.art]
    || (a.uhrzeit || '99').localeCompare(b.uhrzeit || '99')
    || (PRIO[a.aufgabe?.priority ?? 'medium'] - PRIO[b.aufgabe?.priority ?? 'medium'])
    || a.schluessel.localeCompare(b.schluessel));
}

/** Je Tag gruppiert; '' = Ablage. */
export function nachTag(liste: Eintrag[]): Map<string, Eintrag[]> {
  const m = new Map<string, Eintrag[]>();
  for (const e of liste) {
    if (!m.has(e.datum)) m.set(e.datum, []);
    m.get(e.datum)!.push(e);
  }
  for (const [k, v] of m) m.set(k, sortiert(v));
  return m;
}
